import React, { useState, useEffect, useMemo } from 'react';
import { subscribeToUserCollection, addUserDoc } from '../utils/userDataHelper';
import { useNavigate } from 'react-router-dom';
import Breadcrumbs from '../Components/Breadcrumbs';
import Footer from '../Components/Footer';
import SafetyNotice from '../Components/SafetyNotice';
import './labcss/recipe-pages.css';
import { normalizeRecipe, weightToGrams } from '../utils/recipeSchema';
import { calculateRecipe as calculateFormula } from './utils/recipeCalculations';
import { useAuth } from '../contexts/AuthContext';
import { computeMaterialCost, buildTotals, computePricing, getCostPerUnit } from './utils/costCalculations';
import useCurrencyTools from '../hooks/useCurrencyTools';

// Classic soap recipe templates with traditional formulations
const CLASSIC_TEMPLATES = [
  {
    id: 'castile',
    name: 'Pure Castile Soap',
    description: '100% Olive Oil - The gentlest, most conditioning soap. Traditional Spanish recipe dating back centuries.',
    difficulty: 'Beginner',
    cureTime: '8-12 weeks',
    properties: { hardness: 'Low', lather: 'Creamy', conditioning: 'Very High' },
    oils: [{ name: 'Olive Oil', key: 'olive_oil', pct: 100 }],
    additives: [],
    superfat: 7,
    lyeConcentration: 0.28,
    tips: 'Requires longer cure time (8-12 weeks) for best results. Makes a very soft bar initially.',
    use: ['face', 'body'],
  },
  {
    id: 'marseille',
    name: 'Savon de Marseille',
    description: '72% Olive Oil - Traditional French soap with coconut for better lather.',
    difficulty: 'Beginner',
    cureTime: '4-6 weeks',
    properties: { hardness: 'Medium', lather: 'Good', conditioning: 'High' },
    oils: [
      { name: 'Olive Oil', key: 'olive_oil', pct: 72 },
      { name: 'Coconut Oil', key: 'coconut_oil', pct: 18 },
      { name: 'Palm Oil', key: 'palm_oil', pct: 10 }
    ],
    additives: [],
    superfat: 6,
    lyeConcentration: 0.30,
    tips: 'Classic French recipe. Great everyday soap with good lather.',
    use: ['body'],
  },
  {
    id: 'bastille',
    name: 'Bastille Soap',
    description: '70%+ Olive Oil - Similar to Castile but with added oils for better lather.',
    difficulty: 'Beginner',
    cureTime: '6-8 weeks',
    properties: { hardness: 'Medium', lather: 'Good', conditioning: 'Very High' },
    oils: [
      { name: 'Olive Oil', key: 'olive_oil', pct: 70 },
      { name: 'Coconut Oil', key: 'coconut_oil', pct: 20 },
      { name: 'Castor Oil', key: 'castor_oil', pct: 10 }
    ],
    additives: [],
    superfat: 7,
    lyeConcentration: 0.30,
    tips: 'The castor oil boosts lather significantly. Great for beginners.',
    use: ['face', 'body'],
  },
  {
    id: 'shea_luxury',
    name: 'Shea Butter Luxury Bar',
    description: 'Rich, creamy bar with shea butter for a gentle, conditioning feel.',
    difficulty: 'Intermediate',
    cureTime: '4-6 weeks',
    properties: { hardness: 'Medium-High', lather: 'Creamy', conditioning: 'Very High' },
    oils: [
      { name: 'Olive Oil', key: 'olive_oil', pct: 40 },
      { name: 'Coconut Oil', key: 'coconut_oil', pct: 25 },
      { name: 'Shea Butter', key: 'shea_butter', pct: 20 },
      { name: 'Castor Oil', key: 'castor_oil', pct: 5 },
      { name: 'Rice Bran Oil', key: 'rice_bran_oil', pct: 10 }
    ],
    additives: [],
    superfat: 6,
    lyeConcentration: 0.32,
    tips: 'Melt shea butter gently; overheating can make it grainy.',
    use: ['body'],
  },
  {
    id: 'coconut_100',
    name: '100% Coconut (Salt Bar)',
    description: 'All coconut oil - Very cleansing, bubbly lather. Great as salt spa bar.',
    difficulty: 'Beginner',
    cureTime: '2-4 weeks',
    properties: { hardness: 'Very High', lather: 'Bubbly', conditioning: 'Low' },
    oils: [{ name: 'Coconut Oil', key: 'coconut_oil', pct: 100 }],
    additives: [
      { name: 'Sea Salt', key: 'sea_salt', amount: 50, unit: 'g', whenAdded: 'at-trace', type: 'additive' }
    ],
    superfat: 20,
    lyeConcentration: 0.33,
    tips: 'Use 20% superfat to prevent drying. Add salt at trace for spa bars.',
    use: ['body'],
  },
  {
    id: 'three_oil',
    name: 'Classic Three Oil (Beginner)',
    description: 'Simple, balanced recipe with just three oils. Perfect for learning.',
    difficulty: 'Beginner',
    cureTime: '4-6 weeks',
    properties: { hardness: 'Medium', lather: 'Good', conditioning: 'Medium-High' },
    oils: [
      { name: 'Olive Oil', key: 'olive_oil', pct: 50 },
      { name: 'Coconut Oil', key: 'coconut_oil', pct: 30 },
      { name: 'Palm Oil', key: 'palm_oil', pct: 20 }
    ],
    additives: [],
    superfat: 5,
    lyeConcentration: 0.30,
    tips: 'Great starter recipe. Well-balanced bar with predictable behavior.',
    use: ['body'],
  },
  {
    id: 'facial_gentle',
    name: 'Gentle Facial Bar',
    description: 'Low-cleansing, high-conditioning bar for face. Very gentle.',
    difficulty: 'Intermediate',
    cureTime: '6-8 weeks',
    properties: { hardness: 'Medium', lather: 'Low-Creamy', conditioning: 'Very High' },
    oils: [
      { name: 'Olive Oil', key: 'olive_oil', pct: 50 },
      { name: 'Rice Bran Oil', key: 'rice_bran_oil', pct: 20 },
      { name: 'Shea Butter', key: 'shea_butter', pct: 15 },
      { name: 'Coconut Oil', key: 'coconut_oil', pct: 10 },
      { name: 'Castor Oil', key: 'castor_oil', pct: 5 }
    ],
    additives: [
      { name: 'Kaolin Clay', key: 'kaolin_clay', amount: 15, unit: 'g', whenAdded: 'at-trace', type: 'clay', optional: true }
    ],
    superfat: 8,
    lyeConcentration: 0.28,
    tips: 'Keep coconut low for face bars. Kaolin clay adds silkiness.',
    use: ['face'],
  },
  {
    id: 'cocoa_indulgent',
    name: 'Cocoa Butter Indulgent',
    description: 'Rich chocolate-scented luxury bar. Cocoa butter creates hard, smooth bar.',
    difficulty: 'Intermediate',
    cureTime: '4-6 weeks',
    properties: { hardness: 'High', lather: 'Creamy', conditioning: 'High' },
    oils: [
      { name: 'Olive Oil', key: 'olive_oil', pct: 35 },
      { name: 'Coconut Oil', key: 'coconut_oil', pct: 25 },
      { name: 'Cocoa Butter', key: 'cocoa_butter', pct: 15 },
      { name: 'Palm Oil', key: 'palm_oil', pct: 15 },
      { name: 'Castor Oil', key: 'castor_oil', pct: 10 }
    ],
    additives: [
      { name: 'Cocoa Powder', key: 'cocoa_powder', amount: 10, unit: 'g', whenAdded: 'at-trace', type: 'additive', optional: true }
    ],
    superfat: 6,
    lyeConcentration: 0.32,
    tips: 'Cocoa butter speeds up trace. Work quickly. Natural chocolate scent!',
    use: ['body'],
  },
  {
    id: 'avocado_hair',
    name: 'Avocado Shampoo Bar',
    description: 'Solid shampoo bar with avocado oil. Good for hair and scalp.',
    difficulty: 'Advanced',
    cureTime: '4-6 weeks',
    properties: { hardness: 'Medium', lather: 'Bubbly', conditioning: 'Medium-High' },
    oils: [
      { name: 'Coconut Oil', key: 'coconut_oil', pct: 33 },
      { name: 'Olive Oil', key: 'olive_oil', pct: 32 },
      { name: 'Avocado Oil', key: 'avocado_oil', pct: 20 },
      { name: 'Castor Oil', key: 'castor_oil', pct: 15 }
    ],
    additives: [],
    superfat: 3,
    lyeConcentration: 0.30,
    tips: 'Coconut and castor at their usual maximums give plenty of lather. Hair bars often use a slightly lower superfat.',
    use: ['hair'],
  },
];

// Ayurvedic soap templates - Traditional Indian formulations
const AYURVEDIC_TEMPLATES = [
  {
    id: 'haldi_chandan',
    name: 'Haldi Chandan (Turmeric Sandalwood)',
    description: 'Inspired by the traditional haldi-chandan bridal ritual: turmeric for a warm golden colour, sandalwood for scent.',
    difficulty: 'Intermediate',
    cureTime: '4-6 weeks',
    properties: { hardness: 'Medium', lather: 'Creamy', conditioning: 'High' },
    oils: [
      { name: 'Coconut Oil', key: 'coconut_oil', pct: 30 },
      { name: 'Olive Oil', key: 'olive_oil', pct: 35 },
      { name: 'Sesame Oil', key: 'sesame_oil', pct: 20 },
      { name: 'Castor Oil', key: 'castor_oil', pct: 10 },
      { name: 'Neem Oil', key: 'neem_oil', pct: 5 }
    ],
    additives: [
      { name: 'Turmeric Powder', key: 'turmeric_powder', amount: 10, unit: 'g', whenAdded: 'at-trace', type: 'herb' },
      { name: 'Sandalwood Powder', key: 'sandalwood_powder', amount: 8, unit: 'g', whenAdded: 'at-trace', type: 'herb' },
      { name: 'Sandalwood Essential Oil', key: 'sandalwood_eo', amount: 13, unit: 'g', whenAdded: 'at-trace', type: 'fragrance', optional: true }
    ],
    superfat: 7,
    lyeConcentration: 0.30,
    tips: 'Use no more than about 1 tsp turmeric per 500 g oils; more can stain skin and flannels yellow.',
    use: ['face', 'body'],
  },
  {
    id: 'multani_mitti',
    name: 'Multani Mitti (Fuller\'s Earth)',
    description: 'Clay bar with multani mitti (fuller\'s earth) for a smooth, silky feel. A popular choice for oily skin.',
    difficulty: 'Beginner',
    cureTime: '4-6 weeks',
    properties: { hardness: 'Medium', lather: 'Good', conditioning: 'Medium' },
    oils: [
      { name: 'Coconut Oil', key: 'coconut_oil', pct: 30 },
      { name: 'Olive Oil', key: 'olive_oil', pct: 35 },
      { name: 'Rice Bran Oil', key: 'rice_bran_oil', pct: 20 },
      { name: 'Castor Oil', key: 'castor_oil', pct: 10 },
      { name: 'Neem Oil', key: 'neem_oil', pct: 5 }
    ],
    additives: [
      { name: 'Multani Mitti (Fuller\'s Earth)', key: 'multani_mitti', amount: 25, unit: 'g', whenAdded: 'at-trace', type: 'clay' },
      { name: 'Rose Water', key: 'rose_water', amount: 20, unit: 'g', whenAdded: 'replace-water', type: 'liquid', optional: true }
    ],
    superfat: 6,
    lyeConcentration: 0.30,
    tips: 'Mix multani mitti with a little water before adding so it disperses smoothly.',
    use: ['face'],
  },
  {
    id: 'neem_tulsi',
    name: 'Neem Tulsi Herbal Bar',
    description: 'Herbal bar with neem oil and holy basil (tulsi), a traditional Ayurvedic pairing.',
    difficulty: 'Intermediate',
    cureTime: '4-6 weeks',
    properties: { hardness: 'Medium', lather: 'Creamy', conditioning: 'High' },
    oils: [
      { name: 'Coconut Oil', key: 'coconut_oil', pct: 25 },
      { name: 'Olive Oil', key: 'olive_oil', pct: 45 },
      { name: 'Neem Oil', key: 'neem_oil', pct: 10 },
      { name: 'Rice Bran Oil', key: 'rice_bran_oil', pct: 10 },
      { name: 'Castor Oil', key: 'castor_oil', pct: 10 }
    ],
    additives: [
      { name: 'Tulsi (Holy Basil) Powder', key: 'tulsi_powder', amount: 12, unit: 'g', whenAdded: 'at-trace', type: 'herb' },
      { name: 'Neem Powder', key: 'neem_powder', amount: 10, unit: 'g', whenAdded: 'at-trace', type: 'herb' },
      { name: 'Tea Tree Essential Oil', key: 'tea_tree_eo', amount: 9, unit: 'g', whenAdded: 'at-trace', type: 'fragrance', optional: true }
    ],
    superfat: 7,
    lyeConcentration: 0.30,
    tips: 'Neem oil has a strong smell that carries into the soap; a little tea tree essential oil, within its usage limit, helps balance it.',
    use: ['body'],
  },
  {
    id: 'aloe_vera_cooling',
    name: 'Aloe Vera Cooling Soap',
    description: 'Aloe vera bar with a fresh, light feel. Popular in summer.',
    difficulty: 'Intermediate',
    cureTime: '4-6 weeks',
    properties: { hardness: 'Medium', lather: 'Creamy', conditioning: 'Very High' },
    oils: [
      { name: 'Olive Oil', key: 'olive_oil', pct: 40 },
      { name: 'Coconut Oil', key: 'coconut_oil', pct: 25 },
      { name: 'Rice Bran Oil', key: 'rice_bran_oil', pct: 20 },
      { name: 'Castor Oil', key: 'castor_oil', pct: 10 },
      { name: 'Shea Butter', key: 'shea_butter', pct: 5 }
    ],
    additives: [
      { name: 'Aloe Vera Gel', key: 'aloe_vera_gel', amount: 50, unit: 'g', whenAdded: 'replace-water', type: 'liquid' },
      { name: 'Cucumber Extract', key: 'cucumber_extract', amount: 10, unit: 'g', whenAdded: 'at-trace', type: 'liquid', optional: true },
      { name: 'Peppermint Essential Oil', key: 'peppermint_eo', amount: 7, unit: 'g', whenAdded: 'at-trace', type: 'fragrance', optional: true }
    ],
    superfat: 7,
    lyeConcentration: 0.28,
    tips: 'Fresh aloe gel can replace part of the water: blend it smooth and keep the batch cool. Keep peppermint oil within its usage limit.',
    use: ['face', 'body'],
  },
  {
    id: 'shikakai_reetha',
    name: 'Shikakai Reetha Hair Bar',
    description: 'Solid hair bar with shikakai and reetha, traditional Indian hair-wash herbs.',
    difficulty: 'Advanced',
    cureTime: '4-6 weeks',
    properties: { hardness: 'Medium', lather: 'Bubbly', conditioning: 'Medium-High' },
    oils: [
      { name: 'Coconut Oil', key: 'coconut_oil', pct: 33 },
      { name: 'Olive Oil', key: 'olive_oil', pct: 32 },
      { name: 'Castor Oil', key: 'castor_oil', pct: 15 },
      { name: 'Sesame Oil', key: 'sesame_oil', pct: 20 }
    ],
    additives: [
      { name: 'Shikakai Powder', key: 'shikakai_powder', amount: 20, unit: 'g', whenAdded: 'at-trace', type: 'herb' },
      { name: 'Reetha (Soapnut) Powder', key: 'reetha_powder', amount: 15, unit: 'g', whenAdded: 'at-trace', type: 'herb' },
      { name: 'Amla Powder', key: 'amla_powder', amount: 10, unit: 'g', whenAdded: 'at-trace', type: 'herb' },
      { name: 'Brahmi Powder', key: 'brahmi_powder', amount: 5, unit: 'g', whenAdded: 'at-trace', type: 'herb', optional: true }
    ],
    superfat: 4,
    lyeConcentration: 0.30,
    tips: 'Lower superfat for hair bars. Traditional herbal hair wash. May cause initial adjustment period.',
    use: ['hair'],
  },
  {
    id: 'ubtan_soap',
    name: 'Ubtan Soap',
    description: 'Inspired by traditional ubtan, with besan (gram flour), turmeric and sandalwood powder for a gentle scrub.',
    difficulty: 'Intermediate',
    cureTime: '4-6 weeks',
    properties: { hardness: 'Medium', lather: 'Creamy', conditioning: 'High' },
    oils: [
      { name: 'Coconut Oil', key: 'coconut_oil', pct: 30 },
      { name: 'Olive Oil', key: 'olive_oil', pct: 30 },
      { name: 'Sesame Oil', key: 'sesame_oil', pct: 20 },
      { name: 'Mustard Oil', key: 'mustard_oil', pct: 10 },
      { name: 'Castor Oil', key: 'castor_oil', pct: 10 }
    ],
    additives: [
      { name: 'Besan (Gram Flour)', key: 'besan', amount: 20, unit: 'g', whenAdded: 'at-trace', type: 'scrub' },
      { name: 'Turmeric Powder', key: 'turmeric_powder', amount: 5, unit: 'g', whenAdded: 'at-trace', type: 'herb' },
      { name: 'Sandalwood Powder', key: 'sandalwood_powder', amount: 8, unit: 'g', whenAdded: 'at-trace', type: 'herb' },
      { name: 'Rose Petal Powder', key: 'rose_petal_powder', amount: 5, unit: 'g', whenAdded: 'at-trace', type: 'herb', optional: true }
    ],
    superfat: 7,
    lyeConcentration: 0.30,
    tips: 'Besan adds gentle exfoliation. Keep turmeric low to avoid staining.',
    use: ['face', 'body'],
  },
  {
    id: 'triphala_detox',
    name: 'Triphala Herbal Soap',
    description: 'Ayurveda-inspired bar with triphala powder (amla, haritaki and bibhitaki) for an earthy colour.',
    difficulty: 'Intermediate',
    cureTime: '4-6 weeks',
    properties: { hardness: 'Medium', lather: 'Good', conditioning: 'High' },
    oils: [
      { name: 'Coconut Oil', key: 'coconut_oil', pct: 30 },
      { name: 'Olive Oil', key: 'olive_oil', pct: 35 },
      { name: 'Rice Bran Oil', key: 'rice_bran_oil', pct: 20 },
      { name: 'Castor Oil', key: 'castor_oil', pct: 10 },
      { name: 'Neem Oil', key: 'neem_oil', pct: 5 }
    ],
    additives: [
      { name: 'Triphala Powder', key: 'triphala_powder', amount: 15, unit: 'g', whenAdded: 'at-trace', type: 'herb' },
      { name: 'Activated Charcoal', key: 'activated_charcoal', amount: 5, unit: 'g', whenAdded: 'at-trace', type: 'additive', optional: true }
    ],
    superfat: 6,
    lyeConcentration: 0.30,
    tips: 'Triphala gives a brown-green colour that can darken over time. Disperse the powder in a little oil first to avoid specks.',
    use: ['body'],
  },
  {
    id: 'kumkumadi',
    name: 'Kumkumadi Luxury Soap',
    description: 'Inspired by kumkumadi tailam, a traditional Ayurvedic oil blend, with saffron for colour.',
    difficulty: 'Advanced',
    cureTime: '4-6 weeks',
    properties: { hardness: 'Medium', lather: 'Creamy', conditioning: 'Very High' },
    oils: [
      { name: 'Olive Oil', key: 'olive_oil', pct: 35 },
      { name: 'Coconut Oil', key: 'coconut_oil', pct: 25 },
      { name: 'Sesame Oil', key: 'sesame_oil', pct: 20 },
      { name: 'Shea Butter', key: 'shea_butter', pct: 10 },
      { name: 'Castor Oil', key: 'castor_oil', pct: 10 }
    ],
    additives: [
      { name: 'Saffron (Kesar)', key: 'saffron', amount: 0.5, unit: 'g', whenAdded: 'infuse-oil', type: 'herb' },
      { name: 'Sandalwood Powder', key: 'sandalwood_powder', amount: 10, unit: 'g', whenAdded: 'at-trace', type: 'herb' },
      { name: 'Manjistha Powder', key: 'manjistha_powder', amount: 5, unit: 'g', whenAdded: 'at-trace', type: 'herb' },
      { name: 'Rose Essential Oil', key: 'rose_eo', amount: 4.5, unit: 'g', whenAdded: 'at-trace', type: 'fragrance', optional: true }
    ],
    superfat: 8,
    lyeConcentration: 0.28,
    tips: 'Infuse saffron in warm oil overnight before making. Premium luxury bar. Small batch due to saffron cost.',
    use: ['face'],
  },
  {
    id: 'kesar_malai',
    name: 'Kesar Malai (Saffron Cream)',
    description: 'Luxurious saffron and milk soap. Traditional Indian royal beauty recipe.',
    difficulty: 'Advanced',
    cureTime: '4-6 weeks',
    properties: { hardness: 'Medium', lather: 'Creamy', conditioning: 'Very High' },
    oils: [
      { name: 'Olive Oil', key: 'olive_oil', pct: 35 },
      { name: 'Coconut Oil', key: 'coconut_oil', pct: 25 },
      { name: 'Shea Butter', key: 'shea_butter', pct: 15 },
      { name: 'Rice Bran Oil', key: 'rice_bran_oil', pct: 15 },
      { name: 'Castor Oil', key: 'castor_oil', pct: 10 }
    ],
    additives: [
      { name: 'Saffron (Kesar)', key: 'saffron', amount: 0.3, unit: 'g', whenAdded: 'infuse-milk', type: 'herb' },
      { name: 'Full Cream Milk', key: 'full_cream_milk', amount: 50, unit: 'g', whenAdded: 'replace-water', type: 'liquid' },
      { name: 'Honey', key: 'honey', amount: 15, unit: 'g', whenAdded: 'at-trace', type: 'additive', optional: true }
    ],
    superfat: 8,
    lyeConcentration: 0.28,
    tips: 'Freeze milk before adding to lye. Saffron-infused milk creates beautiful color.',
    use: ['face', 'body'],
  },
  {
    id: 'rose_gulab',
    name: 'Gulab (Rose) Soap',
    description: 'Classic Indian rose soap. Rose water and petals for romantic fragrance.',
    difficulty: 'Beginner',
    cureTime: '4-6 weeks',
    properties: { hardness: 'Medium', lather: 'Good', conditioning: 'High' },
    oils: [
      { name: 'Olive Oil', key: 'olive_oil', pct: 40 },
      { name: 'Coconut Oil', key: 'coconut_oil', pct: 30 },
      { name: 'Rice Bran Oil', key: 'rice_bran_oil', pct: 20 },
      { name: 'Castor Oil', key: 'castor_oil', pct: 10 }
    ],
    additives: [
      { name: 'Rose Water', key: 'rose_water', amount: 30, unit: 'g', whenAdded: 'replace-water', type: 'liquid' },
      { name: 'Rose Petal Powder', key: 'rose_petal_powder', amount: 8, unit: 'g', whenAdded: 'at-trace', type: 'herb' },
      { name: 'Rose Geranium Essential Oil', key: 'rose_geranium_eo', amount: 11, unit: 'g', whenAdded: 'at-trace', type: 'fragrance', optional: true },
      { name: 'Pink Clay', key: 'pink_clay', amount: 10, unit: 'g', whenAdded: 'at-trace', type: 'clay', optional: true }
    ],
    superfat: 6,
    lyeConcentration: 0.30,
    tips: 'Rose scent can fade. Choose a fragrance tested in soap rather than going over the supplier\'s maximum. Pink clay gives a soft colour.',
    use: ['face', 'body'],
  },
];

// Combine all templates
export const RECIPE_TEMPLATES = [...CLASSIC_TEMPLATES, ...AYURVEDIC_TEMPLATES];

const DIFFICULTY_COLORS = {
  'Beginner': '#4caf50',
  'Intermediate': '#ff9800',
  'Advanced': '#f44336',
};

const ADDITIVE_TYPE_ICONS = {
  'herb': '🌿',
  'clay': '🏺',
  'liquid': '💧',
  'fragrance': '🌸',
  'additive': '✨',
  'scrub': '🧂',
};

export default function RecipeTemplates() {
  const [ingredients, setIngredients] = useState([]);
  const { user } = useAuth();
  const { preferredCurrency, convertMoney } = useCurrencyTools();
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [batchSize, setBatchSize] = useState(1000);
  const [barWeight, setBarWeight] = useState(100);
  const [saving, setSaving] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [showMissingModal, setShowMissingModal] = useState(null);
  const navigate = useNavigate();

  // Load ingredients for SAP lookup and inventory check
  useEffect(() => {
    if (!user) { setIngredients([]); return undefined; }
    let unsub = () => {};
    try {
      unsub = subscribeToUserCollection('ingredients', (data) => {
        // Add firebaseId to match expected structure
        const withFirebaseId = data.map(item => ({ ...item, firebaseId: item.id }));
        setIngredients(withFirebaseId);
      });
    } catch (error) {
      console.error('Error loading ingredients:', error);
    }
    return () => unsub();
  }, [user]);

  // Check inventory for missing ingredients
  const checkInventory = (template) => {
    const missingOils = [];
    const missingAdditives = [];
    const lowStock = [];

    // Check oils
    template.oils.forEach(oil => {
      const inv = ingredients.find(i => 
        i.key === oil.key || 
        (i.name || '').toLowerCase().replace(/\s+/g, '_') === oil.key ||
        (i.name || '').toLowerCase() === oil.name.toLowerCase()
      );
      if (!inv) {
        missingOils.push(oil.name);
      } else {
        const requiredWeight = (oil.pct / 100) * batchSize;
        if (weightToGrams(inv.stock || 0, inv.unit || 'g', inv.densityGPerMl) < requiredWeight) {
          lowStock.push({ name: oil.name, have: inv.stock || 0, need: requiredWeight });
        }
      }
    });

    // Check required additives (not optional)
    (template.additives || []).forEach(add => {
      if (add.optional) return;
      const inv = ingredients.find(i => 
        i.key === add.key || 
        (i.name || '').toLowerCase().replace(/\s+/g, '_') === add.key ||
        (i.name || '').toLowerCase() === add.name.toLowerCase()
      );
      if (!inv) {
        missingAdditives.push(add.name);
      }
    });

    return { missingOils, missingAdditives, lowStock, canMake: missingOils.length === 0 && missingAdditives.length === 0 };
  };

  const buildTemplateRecipe = template => {
    const additives = (template.additives || []).map(item => ({ ...item, amount: Number(item.amount) * batchSize / 1000 }));
    const input = normalizeRecipe({ ...template, items: template.oils, additives, totalOilWeight: batchSize, barWeight,
      waterMethod: 'concentration', lyePurity: 100, makePrivate: true });
    const calculated = calculateFormula(input, ingredients);
    if (!calculated.valid) throw new Error(calculated.errors.join(' '));
    const inventory = ingredients.map(item => ({ ...item,
      costPerUnit: convertMoney(getCostPerUnit(item), item.currencyCode || preferredCurrency, preferredCurrency) }));
    const material = computeMaterialCost(inventory, calculated.oils, calculated.lyeRequired,
      calculated.waterWeight, calculated.additives, { lyeType: input.lyeType, alkaliWeights: calculated.alkaliWeights });
    const cost = { material, working: 0, packaging: 0 };
    const totals = buildTotals(cost, calculated.bars);
    return normalizeRecipe({ ...input, items: calculated.oils, oils: calculated.oils,
      lyeRequired: calculated.lyeRequired, waterWeight: calculated.waterWeight,
      soapWeight: calculated.soapWeight, alkaliWeights: calculated.alkaliWeights,
      additives: calculated.additives, bars: calculated.bars, cost, totals,
      pricing: computePricing(totals.perBar), currencyCode: preferredCurrency });
  };
  const calculateRecipe = template => {
    try {
      const result = buildTemplateRecipe(template);
      return { oilsComputed: result.oils, naohBase: (result.lyeRequired / (1 - result.superfat / 100)).toFixed(2),
        adjustedNaoh: result.lyeRequired.toFixed(2), waterWeight: result.waterWeight.toFixed(2),
        totalBatchMass: result.soapWeight.toFixed(2), bars: result.bars };
    } catch (error) { return { oilsComputed: [], naohBase: '—', adjustedNaoh: '—', waterWeight: '—', totalBatchMass: '—', bars: '—' }; }
  };

  const filteredTemplates = useMemo(() => {
    if (filterCategory === 'all') return RECIPE_TEMPLATES;
    if (filterCategory === 'classic') return CLASSIC_TEMPLATES;
    if (filterCategory === 'ayurvedic') return AYURVEDIC_TEMPLATES;
    return RECIPE_TEMPLATES;
  }, [filterCategory]);

  const handleUseTemplate = async (template) => {
    if (!user) { handleEditInCreator(template); return; }

    setSaving(true);
    try {
      const recipeObj = {
        ...buildTemplateRecipe(template),
        name: `${template.name} - ${new Date().toLocaleDateString()}`,
        templateId: template.id, templateName: template.name, notes: template.tips,
        collection: 'try', status: 'draft', version: 1,
      };
      delete recipeObj.id;

      await addUserDoc('recipes', recipeObj);
      alert(`Recipe "${recipeObj.name}" created from template!`);
      navigate('/recipes');
    } catch (err) {
      console.error('Failed to save template recipe', err);
      alert('Failed to create recipe from template');
    } finally {
      setSaving(false);
    }
  };

  const handleEditInCreator = (template) => {
    try {
      const recipeData = normalizeRecipe({ ...template, items: template.oils,
        additives: (template.additives || []).map(item => ({ ...item, amount: Number(item.amount) * batchSize / 1000 })),
        totalOilWeight: batchSize, barWeight, lyePurity: 100, waterMethod: 'concentration', makePrivate: true });
      delete recipeData.id;
      sessionStorage.setItem('editRecipe', JSON.stringify(recipeData));
      navigate('/calculator');
    } catch (error) { alert(error.message); }
  };

  return (
    <>
      <div className="sl-page templates-page">
        <div className="pageheader borderleft">
          <Breadcrumbs items={[
            { label: 'Home', to: '/' },
            { label: 'Recipes', to: '/recipes' },
            { label: 'Templates' },
          ]} />
          <div className="pageheading">Recipe Templates</div>
          <div className="pagedescription">Classic & Ayurvedic starting formulations. Review purity, additives and actual ingredient data in the calculator. Template costs exclude labour and packaging until configured.</div>
        </div>

        <div className="templates-content borderleft">
          <section className="mypadding"><SafetyNotice /></section>
          {/* Category Filter + Batch Size */}
          <section className="mypadding borderbottom">
            <div className="tpl-controls">
              <div className="tpl-tabs">
                {[
                  { key: 'all', label: 'All Templates', count: RECIPE_TEMPLATES.length },
                  { key: 'classic', label: 'Classic', count: CLASSIC_TEMPLATES.length },
                  { key: 'ayurvedic', label: 'Ayurvedic', count: AYURVEDIC_TEMPLATES.length },
                ].map(cat => (
                  <button
                    key={cat.key}
                    className={`tpl-tab${filterCategory === cat.key ? ' active' : ''}`}
                    onClick={() => setFilterCategory(cat.key)}
                  >
                    {cat.label} ({cat.count})
                  </button>
                ))}
              </div>
              <div className="tpl-inputs">
                <div className="tpl-input-group">
                  <label>Oil Weight:</label>
                  <input type="number" value={batchSize} onChange={e => setBatchSize(Number(e.target.value))} />
                  <span>g</span>
                </div>
                <div className="tpl-input-group">
                  <label>Bar:</label>
                  <input type="number" value={barWeight} onChange={e => setBarWeight(Number(e.target.value))} />
                  <span>g</span>
                </div>
              </div>
            </div>
          </section>

          {/* Templates Grid */}
          <section className="mypadding">
            <div className="rcp-grid">
              {filteredTemplates.map(template => {
                const calc = calculateRecipe(template);
                const inventory = checkInventory(template);
                const hasAdditives = (template.additives || []).length > 0;
                const requiredAdditives = (template.additives || []).filter(a => !a.optional);
                const optionalAdditives = (template.additives || []).filter(a => a.optional);

                return (
                  <div
                    key={template.id}
                    className={`tpl-card${selectedTemplate === template.id ? ' selected' : ''}${!inventory.canMake ? ' unavailable' : ''}`}
                    onClick={() => setSelectedTemplate(selectedTemplate === template.id ? null : template.id)}
                  >
                    {/* Header */}
                    <div className="tpl-card-header">
                      <div className="tpl-card-title-row">
                        <div>
                          <div className="tpl-card-title">{template.name}</div>
                          {!inventory.canMake && (
                            <div className="tpl-card-missing">
                              Missing {inventory.missingOils.length + inventory.missingAdditives.length} ingredient(s)
                            </div>
                          )}
                        </div>
                        <span
                          className="tpl-card-difficulty"
                          style={{
                            background: DIFFICULTY_COLORS[template.difficulty] + '20',
                            color: DIFFICULTY_COLORS[template.difficulty],
                          }}
                        >
                          {template.difficulty}
                        </span>
                      </div>
                      <p className="tpl-card-desc">{template.description}</p>
                    </div>

                    {/* Oil Breakdown */}
                    <div className="tpl-oils">
                      <div className="tpl-oils-label">Oils Required:</div>
                      <div className="tpl-oil-chips">
                        {template.oils.map(o => {
                          const inInventory = ingredients.some(i =>
                            i.key === o.key ||
                            (i.name || '').toLowerCase().replace(/\s+/g, '_') === o.key ||
                            (i.name || '').toLowerCase() === o.name.toLowerCase()
                          );
                          return (
                            <span key={o.key} className={`tpl-oil-chip ${inInventory ? 'available' : 'missing'}`}>
                              {inInventory ? '✓' : '✗'} {o.name}: {o.pct}%
                            </span>
                          );
                        })}
                      </div>
                    </div>

                    {/* Additives */}
                    {hasAdditives && (
                      <div className="tpl-additives">
                        <div className="tpl-additives-label">Additives:</div>
                        {requiredAdditives.length > 0 && (
                          <div style={{ marginBottom: 6 }}>
                            <span style={{ fontSize: '0.7em', color: '#666', fontWeight: 500 }}>Required:</span>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                              {requiredAdditives.map(a => {
                                const inInventory = ingredients.some(i =>
                                  i.key === a.key ||
                                  (i.name || '').toLowerCase() === a.name.toLowerCase()
                                );
                                return (
                                  <span key={a.key || a.name} className={`tpl-additive-chip ${inInventory ? 'tpl-additive-req' : 'tpl-additive-missing'}`}>
                                    {ADDITIVE_TYPE_ICONS[a.type] || '•'} {a.name} ({a.amount}{a.unit})
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {optionalAdditives.length > 0 && (
                          <div>
                            <span style={{ fontSize: '0.7em', color: '#666', fontStyle: 'italic' }}>Optional:</span>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                              {optionalAdditives.map(a => (
                                <span key={a.key || a.name} className="tpl-additive-chip tpl-additive-opt">
                                  {ADDITIVE_TYPE_ICONS[a.type] || '•'} {a.name}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Properties */}
                    <div className="tpl-props">
                      <div>
                        <div className="label">Hardness</div>
                        <div className="value">{template.properties.hardness}</div>
                      </div>
                      <div>
                        <div className="label">Lather</div>
                        <div className="value">{template.properties.lather}</div>
                      </div>
                      <div>
                        <div className="label">Cure Time</div>
                        <div className="value">{template.cureTime}</div>
                      </div>
                    </div>

                    {/* Calculated Values */}
                    <div className="tpl-calc">
                      <div>
                        <div className="label">NaOH</div>
                        <div className="value">{calc.adjustedNaoh}g</div>
                      </div>
                      <div>
                        <div className="label">Water</div>
                        <div className="value">{calc.waterWeight}g</div>
                      </div>
                      <div>
                        <div className="label">Total</div>
                        <div className="value">{calc.totalBatchMass}g</div>
                      </div>
                      <div>
                        <div className="label">Bars</div>
                        <div className="value">~{calc.bars}</div>
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {selectedTemplate === template.id && (
                      <div className="tpl-detail">
                        <div className="tpl-detail-tips">
                          <strong>Tips:</strong> {template.tips}
                        </div>
                        <div className="tpl-detail-meta">
                          <span>Superfat: <strong>{template.superfat}%</strong></span>
                          <span>Lye Conc: <strong>{(template.lyeConcentration * 100).toFixed(0)}%</strong></span>
                          <span>Use: <strong>{template.use.join(', ')}</strong></span>
                        </div>

                        {inventory.lowStock.length > 0 && (
                          <div className="tpl-low-stock">
                            <strong>Low Stock:</strong>
                            {inventory.lowStock.map(item => (
                              <div key={item.name}>{item.name}: Have {item.have.toFixed(0)}g, Need {item.need.toFixed(0)}g</div>
                            ))}
                          </div>
                        )}

                        <div className="tpl-actions">
                          <button
                            className="tpl-action-btn tpl-action-save"
                            onClick={(e) => { e.stopPropagation(); handleUseTemplate(template); }}
                            disabled={saving}
                          >
                            {user ? 'Save as Recipe' : 'Open in Calculator'}
                          </button>
                          <button
                            className="tpl-action-btn tpl-action-edit"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditInCreator(template);
                            }}
                          >
                            Customize first
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* Info Footer */}
          <section className="mypadding">
            <div className="tpl-info">
              <strong>About These Templates:</strong> Classic & Ayurvedic recipes used by soap makers for generations.
              Inventory marks help plan purchases. You can calculate and customize any template; production requires the recorded stock.
            </div>
          </section>
        </div>
      

      {/* Missing Ingredients Modal */}
      {showMissingModal && (
        <div className="rcp-modal-overlay" onClick={() => setShowMissingModal(null)}>
          <div className="rcp-modal" style={{ maxWidth: 500 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px', color: '#dc2626' }}>Missing Ingredients</h3>
            <p style={{ fontSize: '0.9em', color: '#64748b', marginBottom: 16 }}>
              You need to add these ingredients to your inventory before using the <strong>{showMissingModal.template.name}</strong> template:
            </p>

            {showMissingModal.inventory.missingOils.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>Missing Oils:</div>
                {showMissingModal.inventory.missingOils.map(oil => (
                  <div key={oil} className="tpl-additive-chip tpl-additive-missing" style={{ display: 'block', marginBottom: 4, padding: '6px 12px' }}>
                    {oil}
                  </div>
                ))}
              </div>
            )}

            {showMissingModal.inventory.missingAdditives.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>Missing Additives:</div>
                {showMissingModal.inventory.missingAdditives.map(add => (
                  <div key={add} className="tpl-additive-chip" style={{ display: 'block', marginBottom: 4, padding: '6px 12px', background: '#fffbeb', color: '#b45309' }}>
                    {add}
                  </div>
                ))}
              </div>
            )}

            <div className="tpl-actions" style={{ marginTop: 20 }}>
              <button
                className="tpl-action-btn tpl-action-inventory"
                onClick={() => { setShowMissingModal(null); navigate('/inventory'); }}
              >
                Go to inventory
              </button>
              <button
                className="tpl-action-btn"
                style={{ background: '#94a3b8' }}
                onClick={() => setShowMissingModal(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      <Footer />
</div>
    </>
  );
}
